// config/executionApiEndpoints.js
//
// Batch-execution endpoints for the Execution Studio (Task 6 / §6a).
//
// Grouping persistence is not here — see the note below.

import { API_BASE_URL } from '../../../shared/config/apiConfig';

export const EXECUTION_API_BASE = API_BASE_URL;

// Groupings are NOT reached through an API. They are JSON files written with the
// app's existing folder/file endpoints — see GroupingService. A
// `/v1.0/groupings/...` API exists in the Batch Execution spec, but its
// API-Gateway routes are not deployed (PR #307) and every call 403s on
// preflight, so there is deliberately nothing here to reach for.

/**
 * The Middleware endpoints this feature calls: three writes and one read.
 *
 * Nine others were deleted (TONIC-PEX-CHG-FE-001). Everything else the Studio
 * needs — grouping CRUD, batch rehydrate, re-run — is served by file and run
 * endpoints that already exist and are already routed. Calling a deleted route
 * returns `403 Missing Authentication Token` from API Gateway, which looks like
 * an auth failure and is not.
 *
 * Deleted, do not reinstate:
 *   GET  /batches/{batchId}                          → per-run reads, see useBatchRun
 *   GET  /batches/{batchId}/report                   → never implemented
 *   POST /batches/{batchId}/members/{runId}/rerun    → a re-run is a normal single run
 *   GET|POST /projects/{projectId}/groupings         → file API, see GroupingService
 *   GET|PUT|PATCH|DELETE /groupings/{groupingId}     → file API
 *   GET  /groupings/{groupingId}/batches             → the id-scoped one; the
 *                                                      project-scoped
 *                                                      `/projects/{id}/groupings/{gid}/batches`
 *                                                      below is live and is the
 *                                                      one to use
 */
export const BATCH_ENDPOINTS = {
  /** Run a batch. Project-scoped, so tenantId rides the query string. */
  create: (projectId, tenantId) =>
    `${EXECUTION_API_BASE}/projects/${projectId}/batches?tenantId=${encodeURIComponent(tenantId ?? '')}`,
  /** Cancel a whole batch. */
  cancel: (batchId) => `${EXECUTION_API_BASE}/batches/${batchId}/cancel`,
  /** Cancel one run — a batch member or a standalone run. */
  cancelRun: (runId) => `${EXECUTION_API_BASE}/runs/${runId}/cancel`,

  /**
   * A grouping's past batches, newest first, cursor-paged.
   *
   * The only read in this file. `limit` defaults to 20 server-side and caps at
   * 50; `cursor` is omitted for the first page and comes from the previous
   * response's `nextCursor`, which is null on the last page.
   *
   * Unlike `create`, tenancy rides the headers here — the contract lists only
   * `limit` and `cursor` as query params.
   */
  listBatches: (projectId, groupingId, { limit, cursor } = {}) => {
    const query = new URLSearchParams();
    if (limit != null) query.set('limit', String(limit));
    if (cursor) query.set('cursor', cursor);
    const suffix = query.toString() ? `?${query}` : '';
    return `${EXECUTION_API_BASE}/projects/${projectId}/groupings/${groupingId}/batches${suffix}`;
  },
};
